import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observer } from 'rxjs';

import { ShToastFactory, Toast } from '@stylehaven/components';
import { EMPTY_OBSERVER } from '../../helpers/constants';
import { GenericActionFailureToast, GenericActionToast } from '../../helpers/generic-action-toast.helper';
import { LoadingBehaviorSubject } from '../../helpers/loading-behavior-subject.helper';
import { ContextHolderActionItem } from '../../record/entities/action/context-holder-action-item.class';
import { RecordActionItem } from '../../record/entities/action/record-action-item.class';
import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { AbstractActionCreator } from '../../services/actions/abstract-action.creator';
import { ToastService } from '../../toast/service/toast.service';
import { PrintLabelRequest } from '../entities/print.entity';
import { RecordPrintService } from '../services/record-print.service';

@Injectable()
export class PrintLabelActionCreator extends AbstractActionCreator {
    constructor(
        private readonly translateService: TranslateService,
        private toastFactory: ShToastFactory,
        private toastService: ToastService,
        private printService: RecordPrintService
    ) {
        super();
    }

    public getPrintLabelAction(loader$: LoadingBehaviorSubject, isReprint: boolean): RecordActionItem<ApiFragment> {
        return this.getAction(loader$, this.translateService.instant('actions.common.print-label'));
    }

    public getBulkPrintLabelAction(loader$: LoadingBehaviorSubject): RecordActionItem<ApiFragment[]> {
        return this.getAction(loader$, this.translateService.instant('actions.common.print-labels'));
    }

    private getAction(loader$: LoadingBehaviorSubject, term: string): RecordActionItem<ApiFragment | ApiFragment[]> {
        const printLabelAction: ContextHolderActionItem<ApiFragment | ApiFragment[]> = new ContextHolderActionItem(
            this.wrapLabelWhenLoadingStream(loader$, term),
            'CanPrintLabel',
            this.translateService.instant('actions.common.disabled')
        );

        printLabelAction.dynamicEnablePredicate = () => this.disableBasedOnLoader(loader$);
        printLabelAction.callback = (record, observer) => {
            loader$.startLoading();
            const toast: Toast = GenericActionToast(
                this.toastFactory,
                this.translateService.instant('label-print.toast.started')
            );
            this.toastService.add(toast);

            this.handlePrint(record, observer, loader$, toast);
        };

        return printLabelAction;
    }

    private handlePrint(
        record: ApiFragment | ApiFragment[],
        observer: Observer<any>,
        loader$: LoadingBehaviorSubject,
        toast: Toast
    ): void {
        const records: ApiFragment[] = this.castToList(record);
        const request: PrintLabelRequest[] = records.map(record => ({ record }));
        this.printLabels(request, loader$, toast, observer);
    }

    private printLabels(
        request: PrintLabelRequest[],
        loader$: LoadingBehaviorSubject,
        toast: Toast,
        observer: Observer<any>
    ) {
        this.printService
            .printLabels(request)
            .pipe()
            .subscribe(
                this.mergeObservers(
                    {
                        ...EMPTY_OBSERVER,
                        next: () => {
                            loader$.stopLoading();
                            this.toastService.delete(toast.id);
                        },
                        error: err => {
                            this.toastService.update(
                                toast.id,
                                GenericActionFailureToast(this.translateService.instant('label-print.toast.failed'))
                            );
                        }
                    },
                    observer
                )
            );
    }

    private castToList(record: ApiFragment | ApiFragment[]) {
        if (Array.isArray(record)) {
            return record;
        } else {
            return [record];
        }
    }
}
